import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { defineMessages, FormattedDate, FormattedMessage, FormattedNumber } from "react-intl";
import { Alert, Link } from "@yoast/ui-library";

import formatAmount from "../../functions/currency";
import useLegacyPricingNotice from "../../hooks/useLegacyPricingNotice";
import {
	CONTACT_UPDATE_DEADLINE,
	isPricingMigrationBannerActive,
	PRICE_EFFECTIVE_DATE,
	SUPPORT_URL,
} from "../../config/pricingMigrationConfig";

import * as styles from "./styles.module.scss";

const messages = defineMessages( {
	notice: {
		id: "pricingMigrationBanner.notice",
		defaultMessage: "Starting {effectiveDate}, the renewal price for {plans} will change to {newPrice}. We weren't " +
			"able to reach you at the email address on file, so please {confirmLink} by {updateDeadline} so you receive " +
			"all account and billing communications going forward.",
	},
	// The same sentence with the figure removed, for the customers the server cannot quote one for.
	// Whole message rather than an empty `{newPrice}`, so the surrounding words stay grammatical and
	// translatable: "will change to ." is not a sentence in any locale.
	noticeWithoutPrice: {
		id: "pricingMigrationBanner.noticeWithoutPrice",
		defaultMessage: "Starting {effectiveDate}, the renewal price for {plans} will change. We weren't able to reach " +
			"you at the email address on file, so please {confirmLink} by {updateDeadline} so you receive all account " +
			"and billing communications going forward.",
	},
	// The period is its own message for the same reason the plan list is: joining "€118.80" and "per
	// year" in JavaScript would hardcode English word order into a value a translator cannot reach.
	priceYearly: {
		id: "pricingMigrationBanner.priceYearly",
		defaultMessage: "{amount} per year",
	},
	priceMonthly: {
		id: "pricingMigrationBanner.priceMonthly",
		defaultMessage: "{amount} per month",
	},
	confirmLink: {
		id: "pricingMigrationBanner.confirmLink",
		defaultMessage: "confirm your contact details",
	},
	questions: {
		id: "pricingMigrationBanner.questions",
		defaultMessage: "Questions? {supportLink}.",
	},
	supportLink: {
		id: "pricingMigrationBanner.supportLink",
		defaultMessage: "Contact support",
	},
	// The plan list is its own message so the Oxford-comma-free "A and B" joining stays translatable
	// rather than being hardcoded with a comma in JavaScript.
	plans: {
		id: "pricingMigrationBanner.plans",
		defaultMessage: "{count, plural, one {your {planList} plan} other {your {planList} plans}}",
	},
	// Deliberately vague, and only ever rendered for a customer the server said is eligible: the notice
	// must still be legible if a plan somehow carries no name.
	plansFallback: {
		id: "pricingMigrationBanner.plansFallback",
		defaultMessage: "your subscription",
	},
} );

/**
 * The notice legal requires MyYoast to carry about the packaging-and-pricing migration.
 *
 * Yoast/Platform#4576. Shown for thirty days to the customers we were never able to reach who still
 * hold a plan whose renewal price changes, so that they can correct their contact details before they
 * are migrated.
 *
 * **Not dismissible, on purpose.** Every other banner in this app can be closed and remembers it in a
 * cookie; this one cannot, because legal requires it to be visible for the whole window. Do not add a
 * close button without checking that requirement has changed.
 *
 * Whether a given customer is in the cohort is entirely the server's answer, and so is the figure the
 * sentence names — see `useLegacyPricingNotice`. This component decides only whether the window is
 * open, and which of the two sentences to render: the price is stated only for the one affected SKU
 * the migration replaces with another product, so most of the cohort sees the variant without it.
 *
 * @returns {JSX.Element|null} The notice, or null when it does not apply.
 */
const PricingMigrationBanner = () => {
	// Asked before the hook's result is used, but not before the hook runs: calling the hook
	// conditionally would break the rules of hooks. Threaded into its `enabled` instead, so a closed
	// window costs no request rather than one per navigation — this is mounted on every logged-in page.
	const isActive = isPricingMigrationBannerActive();
	const { notice, isLoading } = useLegacyPricingNotice( isActive );

	if ( ! isActive || isLoading || ! notice.isEligible ) {
		return null;
	}

	// Intl.ListFormat gives "A, B and C" for the locale rather than a hand-joined string. The app is
	// English-only today, which is exactly why the joining should not be hardcoded now.
	//
	// The locale is hardcoded rather than read from react-intl because this project is pinned to
	// react-intl 2.9, which has no `useIntl` — the locale is only reachable through `injectIntl`, and
	// wrapping the component to thread a constant "en" through it buys nothing today. Whoever adds a
	// second locale should take it from the provider here.
	const planValues = {
		count: notice.affectedPlans.length,
		planList: new Intl.ListFormat( "en", { style: "long", type: "conjunction" } )
			.format( notice.affectedPlans ),
	};
	const plansMessage = notice.affectedPlans.length > 0 ? messages.plans : messages.plansFallback;
	const plans = <FormattedMessage { ...plansMessage } values={ planValues } />;

	// Formatted like every other amount in the app: minor units through `formatAmount`, then
	// `FormattedNumber` for the locale's own symbol placement and separators.
	const newPrice = notice.newPrice && (
		<FormattedMessage
			{ ...( notice.newPrice.billingTerm === "month" ? messages.priceMonthly : messages.priceYearly ) }
			values={ {
				amount: (
					<FormattedNumber
						value={ formatAmount( notice.newPrice.amount ) }
						style="currency"
						currency={ notice.newPrice.currency }
					/>
				),
			} }
		/>
	);

	return (
		<Alert as="div" variant="info" role="status" className={ styles.banner }>
			<div className={ styles.body }>
				<p>
					<FormattedMessage
						{ ...( newPrice ? messages.notice : messages.noticeWithoutPrice ) }
						values={ {
							effectiveDate: <FormattedDate value={ PRICE_EFFECTIVE_DATE } year="numeric" month="long" day="numeric" />,
							plans,
							newPrice,
							updateDeadline: <FormattedDate value={ CONTACT_UPDATE_DEADLINE } year="numeric" month="long" day="numeric" />,
							confirmLink: (
								// A router link, not an anchor: the profile page is in this app, and a full page load
								// would drop the customer out of the SPA on the way to fixing their details.
								<Link as={ RouterLink } to="/profile">
									<FormattedMessage { ...messages.confirmLink } />
								</Link>
							),
						} }
					/>
				</p>
				<p className={ styles.questions }>
					<FormattedMessage
						{ ...messages.questions }
						values={ {
							supportLink: (
								<Link href={ SUPPORT_URL } target="_blank" rel="noopener noreferrer">
									<FormattedMessage { ...messages.supportLink } />
								</Link>
							),
						} }
					/>
				</p>
			</div>
		</Alert>
	);
};

export default PricingMigrationBanner;
